import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

interface Inventory {
    captured_at: string;
    indexed_candidates: number;
    retained_candidates: number;
    unindexed_candidates: number;
    country_missing: number;
    country_assumption?: {
        region: string; confidence: string; assumed_candidates: number;
        indian_mobile_shape: number; requires_format_review: number;
    };
    distinct_stored_e164_formats: { indian?: number; non_indian?: number; invalid?: number };
    country_attribution: { region: string | null; confidence: string; candidates: number }[];
    address_states: {
        available: boolean; phones_with_address: number; phones_with_state: number;
        multiple_state_phones: number; by_state: { state: string; phones: number }[];
    };
}

export default function PhoneInventoryPanel({ expanded = false }: { expanded?: boolean }) {
    const [open, setOpen] = useState(expanded);
    const inFlight = useRef(false);
    const [data, setData] = useState<Inventory | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const load = useCallback(async (fresh = false) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true); setError('');
        try { setData((await api.get('/mulehunter/phone-inventory', { params: { fresh } })).data); }
        catch { setError('Phone inventory is unavailable. Counts have not been confirmed.'); }
        finally { inFlight.current = false; setLoading(false); }
    }, []);
    useEffect(() => { if (expanded) void load(); }, [expanded, load]);
    return <details open={open} onToggle={e => { setOpen(e.currentTarget.open); if (e.currentTarget.open && !data && !loading) void load(); }} className="rounded-lg border border-border bg-card p-4">
        <summary className="cursor-pointer text-sm font-semibold">Phone inventory and address-state evidence</summary>
        <div className="mt-3 space-y-3 text-sm">
            <button onClick={() => void load(true)} disabled={loading} className="rounded border border-border px-3 py-1 text-xs">
                {loading ? 'Checking...' : 'Refresh inventory'}
            </button>
            {error && <p role="alert" className="text-destructive">{error}</p>}
            {data && <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {([
                        ['Indexed phone candidates', data.indexed_candidates],
                        ['Retained across harvest stores', data.retained_candidates],
                        ['Awaiting index reconciliation', data.unindexed_candidates],
                        [data.country_assumption ? 'India assumed; country unverified' : 'Country unresolved in index', data.country_assumption?.assumed_candidates ?? data.country_missing],
                    ] as const).map(([label, count]) => <div key={label} className="rounded bg-muted/50 p-3">
                        <div className="text-xl font-semibold tabular-nums">{count.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                    </div>)}
                </div>
                <p className="text-xs text-muted-foreground">As of {new Date(data.captured_at).toLocaleString('en-IN')}. Retained candidates include historical observations. They are not verified people, active SIMs, offenders or a lifetime collection total.</p>
                {data.country_assumption && <p className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                    India is an operator assumption for {data.country_assumption.assumed_candidates.toLocaleString()} candidates with missing country metadata.
                    {' '}{data.country_assumption.indian_mobile_shape.toLocaleString()} have an Indian mobile shape;
                    {' '}{data.country_assumption.requires_format_review.toLocaleString()} need format review before Indian-only checks.
                    {' '}Stored country evidence is unchanged. Explicit foreign numbers are held out of Indian-only investigations.
                </p>}
                <p className="text-xs">Distinct stored formats: valid India {data.distinct_stored_e164_formats.indian ?? 0}; valid other countries {data.distinct_stored_e164_formats.non_indian ?? 0}; invalid {data.distinct_stored_e164_formats.invalid ?? 0}. Number format does not verify ownership or location.</p>
                <details><summary className="cursor-pointer text-xs font-medium">Country attribution and its basis</summary>
                    <table className="mt-2 w-full text-xs"><thead><tr><th className="text-left">Region</th><th className="text-left">Basis</th><th className="text-right">Candidates</th></tr></thead><tbody>
                        {data.country_attribution.map(r => <tr key={`${r.region}-${r.confidence}`}><td>{r.region || 'Unresolved'}</td><td>{r.confidence}</td><td className="text-right tabular-nums">{r.candidates}</td></tr>)}
                    </tbody></table>
                </details>
                {data.address_states.available ? <>
                    <p className="text-xs">Cached addresses cover {data.address_states.phones_with_address} phones; {data.address_states.phones_with_state} have state evidence, including {data.address_states.multiple_state_phones} with multiple states. These are historical provider address records; current residence is unverified. State counts overlap.</p>
                    <table className="w-full text-xs"><thead><tr><th className="text-left">State in cached address</th><th className="text-right">Phones</th></tr></thead><tbody>
                        {data.address_states.by_state.map(r => <tr key={r.state}><td>{r.state}</td><td className="text-right tabular-nums">{r.phones}</td></tr>)}
                    </tbody></table>
                </> : <p className="text-xs text-muted-foreground">Cached address evidence is unavailable for this deployment.</p>}
            </>}
        </div>
    </details>;
}
